import React, { createContext, useContext, useState, useEffect } from 'react';
import { Snackbar, Alert, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

function SlideTransition(props: TransitionProps & { children: React.ReactElement<any, any> }) {
  return <Slide {...props} direction="up" />;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  // Opcional: identificar variante seleccionada para distinguir items en el carrito
  variantIndex?: number | null;
  variantLabel?: string;
}

interface CartItem {
  product: Product;
  quantity: number;
  key: string; // clave compuesta para distinguir variantes
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (key: string) => void;
  updateQuantity: (key: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    loadCart();
  }, []);

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const loadCart = () => {
    try {
      const cartData = localStorage.getItem('cart');
      if (cartData) {
        const parsed: CartItem[] = JSON.parse(cartData);
        // Asegurar clave para items antiguos sin key
        const withKeys = parsed.map((it) => ({
          ...it,
          key: it.key || getKey(it.product)
        }));
        setItems(withKeys);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  const saveCart = (cartItems: CartItem[]) => {
    try {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  };

  const getKey = (p: Product) => {
    const v = p.variantIndex;
    return v === null || v === undefined ? p.id : `${p.id}::v${v}`;
  };

  const addItem = (product: Product, quantity: number = 1) => {
    setItems(prevItems => {
      const key = getKey(product);
      const existingItem = prevItems.find(item => item.key === key);
      
      let updatedList = prevItems;
      if (existingItem) {
        const desired = existingItem.quantity + quantity;
        updatedList = prevItems.map(item =>
          item.key === key
            ? { ...item, quantity: desired }
            : item
        );
      } else {
        updatedList = [...prevItems, { product, quantity, key }];
      }

      saveCart(updatedList);
      setSnackbarMessage(`Agregado al carrito: ${product.name}`);
      setSnackbarOpen(true);
      return updatedList;
    });
  };

  const removeItem = (key: string) => {
    setItems(prevItems => {
      const updatedItems = prevItems.filter(item => item.key !== key);
      saveCart(updatedItems);
      return updatedItems;
    });
  };

  const updateQuantity = (key: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(key);
      return;
    }

    setItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.key !== key) return item;
        return { ...item, quantity };
      });
      saveCart(updatedItems);
      return updatedItems;
    });
  };

  const clearCart = () => {
    setItems([]);
    saveCart([]);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems
      }}
    >
      {children}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CartContext.Provider>
  );
};